import { apiService } from "api/apiService";
import {
  myStudentsOverviewFromDto,
  rosterStudentFromDto,
  studentMeetingDetailsFromDto,
  studentMeetingProgressFromDto,
} from "meetings/student-meeting/api/mappers";
import {
  teacherGroupsFromDto,
  teacherRowIdentityToDto,
  teacherRowToDto,
} from "meetings/shared/api/mappers";
import type {
  ClearStudentDataSelection,
  ClearTeacherDataSelection,
  MeetingMyStudentsOverview,
  MeetingMyStudentsOverviewDto,
  StudentMeetingDetails,
  StudentMeetingDetailsDto,
  StudentMeetingProgress,
  StudentMeetingProgressDto,
  StudentMeetingRosterStudent,
  StudentMeetingRosterStudentDto,
} from "meetings/student-meeting/types";
import type {
  MeetingTeacherGroups,
  MeetingTeacherGroupsDto,
  MeetingTeacherRow,
  SyncSchedulesResult,
} from "meetings/shared/types";

export const fetchMeetingProgress = async (
  meetingId: number,
): Promise<StudentMeetingProgress> => {
  const { data } = await apiService.get<StudentMeetingProgressDto>(
    `api/meetings/${meetingId}/manage/progress`,
  );
  return studentMeetingProgressFromDto(data);
};

export const fetchMeetingDetails = async (
  meetingId: number,
): Promise<StudentMeetingDetails> => {
  const { data } = await apiService.get<StudentMeetingDetailsDto>(
    `api/meetings/${meetingId}/manage/details`,
  );
  return studentMeetingDetailsFromDto(data);
};

/**
 * One mentor's my-students overview scoped to the managed meeting — the
 * "Gedeelde feedback" sidebar dataset, in the exact shape of the mentor's
 * own "Mijn leerlingen" page.
 */
export const fetchMentorSharedFeedback = async (
  meetingId: number,
  teacherShortage: string,
): Promise<MeetingMyStudentsOverview> => {
  const { data } = await apiService.get<MeetingMyStudentsOverviewDto>(
    `api/meetings/${meetingId}/manage/shared-feedback/${encodeURIComponent(teacherShortage)}`,
  );
  return myStudentsOverviewFromDto(data);
};

export const inviteCoworkers = async (meetingId: number): Promise<void> => {
  await apiService.post(`api/meetings/${meetingId}/manage/invite-coworkers`);
};

export const syncGrades = async (meetingId: number): Promise<void> => {
  await apiService.post(`api/meetings/${meetingId}/manage/sync-grades`);
};

export const syncSchedules = async (
  meetingId: number,
  preview: boolean,
): Promise<SyncSchedulesResult> => {
  const { data } = await apiService.post<SyncSchedulesResult>(
    `api/meetings/${meetingId}/manage/sync-schedules?preview=${preview}`,
  );
  return data;
};

/** The bespreking's student roster — the options of the cleanup picker. */
export const fetchMeetingRoster = async (
  meetingId: number,
): Promise<StudentMeetingRosterStudent[]> => {
  const { data } = await apiService.get<{
    items: StudentMeetingRosterStudentDto[];
  }>(`api/meetings/${meetingId}/manage/students`);
  return data.items.map(rosterStudentFromDto);
};

export const clearStudentData = async (
  meetingId: number,
  selection: ClearStudentDataSelection,
): Promise<void> => {
  await apiService.post(`api/meetings/${meetingId}/manage/clear-student-data`, {
    student_ids: selection.studentIds,
    clear_student_traffic_lights: selection.clearStudentTrafficLights,
    clear_student_remarks: selection.clearStudentRemarks,
  });
};

export const clearTeacherData = async (
  meetingId: number,
  selection: ClearTeacherDataSelection,
): Promise<void> => {
  await apiService.post<void>(
    `api/meetings/${meetingId}/manage/clear-teacher-data`,
    {
      teacher_shortages: selection.teacherShortages,
      clear_teacher_traffic_lights: selection.clearTeacherTrafficLights,
      clear_teacher_feedback: selection.clearTeacherFeedback,
    },
  );
};

/** Moves the whole planning so the meeting starts on `opensAtDate` (YYYY-MM-DD). */
export const shiftPlanning = async (
  meetingId: number,
  opensAtDate: string,
): Promise<void> => {
  await apiService.post<void>(
    `api/meetings/${meetingId}/manage/shift-planning`,
    { opens_at_date: opensAtDate },
  );
};

export const shareAllFeedback = async (meetingId: number): Promise<void> => {
  const formData = new FormData();
  formData.set("meeting_id", String(meetingId));
  await apiService.post("api/share-feedback-bulk", formData);
};

export const deleteStudentMeeting = async (
  meetingId: number,
): Promise<void> => {
  await apiService.delete(`api/meetings/${meetingId}`);
};

export const addClassesAndSubjects = async (
  meetingId: number,
  mentorClasses: string[],
  subjects: string[],
): Promise<void> => {
  await apiService.post(`api/meetings/${meetingId}/manage/classes-subjects`, {
    mentor_classes: mentorClasses,
    subjects,
  });
};

export const removeClassesAndSubjects = async (
  meetingId: number,
  mentorClasses: string[],
  subjects: string[],
): Promise<void> => {
  await apiService.post(
    `api/meetings/${meetingId}/manage/classes-subjects/remove`,
    {
      mentor_classes: mentorClasses,
      subjects,
    },
  );
};

export const fetchMeetingTeachers = async (
  meetingId: number,
): Promise<MeetingTeacherGroups> => {
  const { data } = await apiService.get<{ items: MeetingTeacherGroupsDto }>(
    `api/meetings/${meetingId}/manage/teachers`,
  );
  return teacherGroupsFromDto(data.items);
};

export const updateTemporaryTeacher = async (
  meetingId: number,
  row: MeetingTeacherRow,
  isExtra: boolean,
): Promise<string> => {
  // The endpoint answers 200 with a user-facing `error` message when the
  // swap/insert collided with an existing rated row (legacy contract).
  const { data } = await apiService.put<{ error: string }>(
    `api/meetings/${meetingId}/manage/teachers`,
    {
      schedule: teacherRowToDto(row),
      extra: isExtra,
    },
  );
  return data.error;
};

export const removeTeacher = async (
  meetingId: number,
  row: MeetingTeacherRow,
): Promise<void> => {
  await apiService.post(
    `api/meetings/${meetingId}/manage/teachers/remove`,
    teacherRowIdentityToDto(row),
  );
};
